import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { RentalWithRelations } from "@/types/models";
import { format, differenceInMinutes } from "date-fns";
import { Clock, User, Bike, Calendar, DollarSign } from "lucide-react";

interface GroupedRentalCompletionModalProps {
  rentals: RentalWithRelations[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

const GroupedRentalCompletionModal = ({ 
  rentals, 
  isOpen, 
  onClose, 
  onConfirm, 
  isLoading 
}: GroupedRentalCompletionModalProps) => {
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsConfirming(false);
    }
  }, [isOpen]);

  const primaryRental = rentals[0];
  const startTime = primaryRental.start_date ? format(new Date(primaryRental.start_date), 'dd/MM/yy hh:mm a') : '';
  const expectedReturnTime = primaryRental.expected_return_time ? format(new Date(primaryRental.expected_return_time), 'dd/MM/yy hh:mm a') : '';
  
  const calculateActualDuration = () => {
    if (primaryRental.start_date) {
      const now = new Date();
      const start = new Date(primaryRental.start_date);
      const minutes = differenceInMinutes(now, start);
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    }
    return '0h 0m';
  };

  const isOvertime = () => {
    if (primaryRental.expected_return_time) {
      const now = new Date();
      const returnTime = new Date(primaryRental.expected_return_time);
      return differenceInMinutes(now, returnTime) > 0;
    }
    return false;
  };

  const getTotalAmount = () => {
    return rentals.reduce((total, rental) => total + (rental.total_amount || 0), 0);
  };

  const handleConfirm = () => {
    setIsConfirming(true);
    onConfirm();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-bike-600" />
            Finalizar Servicios de {primaryRental.customer?.name}
          </DialogTitle>
          <DialogDescription>
            Confirma la entrega de {rentals.length} bicicleta{rentals.length > 1 ? 's' : ''} y finalización de los alquileres
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Cliente:</span>
              <span className="font-semibold">{primaryRental.customer?.name}</span>
            </div>

            <Separator />

            <div className="space-y-2">
              <span className="text-sm font-medium text-gray-600">Bicicletas alquiladas:</span>
              {rentals.map((rental) => (
                <div key={rental.id} className="flex items-center justify-between p-2 bg-white rounded border">
                  <div className="flex items-center gap-2">
                    <Bike className="h-4 w-4 text-bike-600" />
                    <span className="font-medium">
                      {rental.bike?.model} #{rental.bike?.number}
                    </span>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    ${rental.total_amount}
                  </Badge>
                </div>
              ))}
            </div>

            <Separator />

            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">Inicio:</span>
              <span className="text-sm font-medium">{startTime}</span>
            </div>

            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">Retorno esperado:</span>
              <span className="text-sm font-medium">{expectedReturnTime}</span>
            </div>

            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">Duración real:</span>
              <span className="text-sm font-medium">{calculateActualDuration()}</span>
            </div>

            {isOvertime() && (
              <div className="bg-red-50 border border-red-200 p-2 rounded">
                <span className="text-red-700 text-sm font-medium">
                  ⚠️ Alquiler con tiempo excedido
                </span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">Total:</span>
              <Badge variant="outline" className="font-semibold">
                ${getTotalAmount()}
              </Badge>
            </div>
          </div>

          <div className="bg-bike-50 border border-bike-200 p-3 rounded-lg">
            <p className="text-sm text-bike-700 font-medium">
              ¿Confirmas que el cliente ha entregado todas las bicicletas en buenas condiciones?
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={isLoading || isConfirming}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={isLoading || isConfirming}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {isConfirming ? "Finalizando..." : "Confirmar Entrega"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GroupedRentalCompletionModal;
